export type ConfigCard = {
  title: string;
  description: string;
  to: string;
  icon: "wrench" | "idp" | "integrations" | "scim" | "sync" | "audit" | "plug";
  audience: "tenant" | "platform" | "both";
};

export function buildConfigurationCards(input: {
  role: "platform_admin" | "tenant_admin" | "tenant_operator";
}): ConfigCard[] {
  const base: ConfigCard[] = [
    {
      title: "Onboarding",
      description: "Walk through the recommended setup steps for this tenant.",
      to: "/onboarding",
      icon: "wrench",
      audience: "tenant",
    },
    {
      title: "Identity Providers",
      description: "Create, update, and test IdP connections for this tenant.",
      to: "/identity-providers",
      icon: "idp",
      audience: "tenant",
    },
    {
      title: "Protected Applications",
      description: "Register apps and choose ephemeral, CAEP/SSF, or IdP back-channel revoke cascade.",
      to: "/protected-applications",
      icon: "plug",
      audience: "tenant",
    },
    {
      title: "Integrations",
      description: "ML, BYOT telemetry endpoints, and integration guidance.",
      to: "/integrations",
      icon: "integrations",
      audience: "tenant",
    },
    {
      title: "Settings",
      description: "Tenant preferences and Verdikta feature toggles for this dashboard.",
      to: "/settings",
      icon: "wrench",
      audience: "tenant",
    },
    {
      title: "Audit Log",
      description:
        "Live stream of recent evaluation and API events. For date filters, CSV exports, and compliance summaries, use Reports.",
      to: "/audit",
      icon: "audit",
      audience: "tenant",
    },
  ];

  if (input.role === "platform_admin") {
    base.unshift({
      title: "Platform admin",
      description: "Fleet overview, organizations, tenant operators, and provider console.",
      to: "/admin",
      icon: "wrench",
      audience: "platform",
    });
  }

  base.splice(
    3,
    0,
    {
      title: "IdP Sync",
      description: "Run directory sync and review results (Okta / Entra).",
      to: "/config/idp-sync",
      icon: "sync",
      audience: "tenant",
    },
    {
      title: "SCIM",
      description: "Provision identities via SCIM and validate connectivity.",
      to: "/config/scim",
      icon: "scim",
      audience: "tenant",
    },
    {
      title: "Custom roles",
      description: "Tenant RBAC bundles for dashboard operators (P1-UX-04).",
      to: "/config/roles",
      icon: "wrench",
      audience: "tenant",
    },
    {
      title: "Session capture",
      description:
        "Configure one-call POST /auth/session — resolve IdP subject or UPN with tenant defaults.",
      to: "/config/session-capture",
      icon: "plug",
      audience: "tenant",
    },
    {
      title: "Ingestion Tools",
      description: "Generate request payloads and run test calls without handcrafting API queries.",
      to: "/config/ingestion",
      icon: "plug",
      audience: "tenant",
    },
    {
      title: "IdP diagnostics",
      description:
        "Test IdP connections, run sync, detect orphans, and rotate SCIM tokens from one place.",
      to: "/config/diagnostics",
      icon: "idp",
      audience: "tenant",
    }
  );

  return base;
}
